import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../redux/redux.hook";
import {
  getGenericProducts,
  loadMoreGenericProducts,
} from "./genericProducts.thunk";
import { resetGenericProducts } from "./genericProducts.slice";
import { isError, isIdle, isLoading, isSuccess } from "@/app/utils/asyncStatus";
import {
  GenericProductsRequest,
  GenericProductsType,
} from "./genericProducts.types";
import { FilterRequest } from "../filters/filters.types";

const useGenericProducts = () => {
  const dispatch = useAppDispatch();
  const {
    data,
    status,
    error,
    currentSlug,
    currentType,
    isPaginating,
    ssrHydrated,
    lastRequestKey,
  } = useAppSelector((store) => store.genericProducts);

  const products = useMemo(() => data?.items ?? [], [data]);
  const pagination = data?.pagination ?? null;
  const section = data?.section ?? null;
  const brand = data?.brand ?? null;
  const metaTitle = data?.meta_title ?? null;
  const seoTitle = data?.seo_title ?? null;
  const seoCategoryTitle = data?.seo_category_title ?? null;
  const seoCategoryText = data?.seo_category_text ?? null;

  const hasMore = useMemo(() => {
    if (!pagination) return false;
    return pagination.current_page < pagination.last_page;
  }, [pagination]);

  const fetchProducts = useCallback(
    async (params: GenericProductsRequest) => {
      const result = await dispatch(getGenericProducts(params));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const fetchMore = useCallback(
    (filters?: FilterRequest) => {
      if (!pagination || !currentSlug || !currentType) return;
      if (pagination.current_page >= pagination.last_page) return;

      dispatch(
        loadMoreGenericProducts({
          type: currentType as GenericProductsType,
          slug: currentSlug,
          page: pagination.current_page + 1,
          per_page: pagination.per_page,
          filters,
        }),
      );
    },
    [dispatch, pagination, currentSlug, currentType],
  );

  const reset = useCallback(() => {
    dispatch(resetGenericProducts());
  }, [dispatch]);

  return {
    products,
    pagination,
    section,
    brand,
    metaTitle,
    seoTitle,
    seoCategoryTitle,
    seoCategoryText,
    hasMore,
    currentSlug,
    currentType,
    status,
    error,
    isIdle: isIdle(status),
    isLoading: isLoading(status),
    isSuccess: isSuccess(status),
    isError: isError(status),
    isPaginating,
    ssrHydrated,
    lastRequestKey,
    fetchProducts,
    fetchMore,
    reset,
  };
};

export default useGenericProducts;
